import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Wrench, 
  Search, 
  Calendar, 
  Building2, 
  Users, 
  Globe2, 
  ShieldCheck, 
  Filter, 
  Activity, 
  Sparkles,
  HeartPulse
} from 'lucide-react';

const workshopsData = [
  {
    id: 1,
    type: "Dissemination Event",
    title: "Equity, Quality, and Inclusiveness in Primary Healthcare Delivery",
    collaborator: "Department of Management Studies, IIT Delhi",
    date: "19th May 2023",
    tenure: "2023–24",
    domain: "Healthcare Delivery & Equity",
    scope: "National Policy & Research Dissemination",
    highlight: true
  },
  {
    id: 2,
    type: "Dissemination Event",
    title: "Mobile Clinics in Public Healthcare",
    collaborator: "Jivika Healthcare & IIT Delhi",
    date: "19th May 2023",
    tenure: "2023–24",
    domain: "Healthcare Delivery & Equity",
    scope: "Public Health Infrastructure",
    highlight: false
  },
  {
    id: 3,
    type: "Roundtable",
    title: "Digital Learning in India’s Journey towards Quality Healthcare for All: Capacity Building and Knowledge Dissemination",
    collaborator: "Empower School of Health",
    date: "15th July 2023",
    tenure: "2023–24",
    domain: "Capacity Building & Digital Learning",
    scope: "One-Day Multi-Stakeholder Roundtable",
    highlight: true
  },
  {
    id: 4,
    type: "E-Dialogue",
    title: "Quality, Equity, and Inclusiveness in Urban Healthcare",
    collaborator: "Expert Panel / Moderated Session",
    date: "9th August 2023",
    tenure: "2023–24",
    domain: "Healthcare Delivery & Equity",
    scope: "Urban Public Health Governance",
    highlight: false
  },
  {
    id: 5,
    type: "Inaugural Summit",
    title: "Launch of the 'Quality, Equity, and Inclusiveness in Public Health' Initiative",
    collaborator: "National Consortium & DMS, IIT Delhi",
    date: "26th August 2023",
    tenure: "2023–24",
    domain: "Healthcare Delivery & Equity",
    scope: "National Flagship Initiative",
    highlight: true
  },
  {
    id: 6,
    type: "Roundtable",
    title: "India-Africa Capacity Building Initiative for Medical Products Manufacturing",
    collaborator: "Empower School of Health",
    date: "11th December 2023",
    tenure: "2023–24",
    domain: "International Alliances",
    scope: "Bilateral South-South Collaboration",
    highlight: true
  },
  {
    id: 7,
    type: "Roundtable",
    title: "Combating Anti-Microbial Resistance (AMR) in India",
    collaborator: "William J. Clinton Foundation (WJCF)",
    date: "13th December 2023",
    tenure: "2023–24",
    domain: "Public Health & Policy",
    scope: "Epidemiological Strategy & AMR Stewardship",
    highlight: true
  },
  {
    id: 8,
    type: "Roundtable",
    title: "Cervical Cancer Elimination in India",
    collaborator: "Cervical Cancer Elimination Consortium-India (CCEC-I) & Sabin Vaccine Institute",
    date: "27th – 28th April 2024",
    tenure: "2023–24",
    domain: "Public Health & Policy",
    scope: "Two-Day National Consortium Summit",
    highlight: true
  },
  {
    id: 9,
    type: "Global Webinar",
    title: "Global HPV Consortium Partners' Webinar",
    collaborator: "Cervical Cancer Elimination Consortium – India (CCEC-I)",
    date: "4th June 2024",
    tenure: "2023–24",
    domain: "International Alliances",
    scope: "Global Health Partner Forum",
    highlight: false
  },
  {
    id: 10,
    type: "Dissemination Event",
    title: "MARCH – Climate Change & Sustainable Governance",
    collaborator: "Nangia Co. LLP & IIT Delhi",
    date: "July 2024",
    tenure: "2023–24",
    domain: "Sustainability & Climate Change",
    scope: "Climate Policy & Strategic Transition",
    highlight: false
  },
  {
    id: 11,
    type: "National Committee",
    title: "NTAG Oxygen Infrastructure: National Expert Committee Consultation",
    collaborator: "National Technical Advisory Group (NTAG), Government of India",
    date: "2022–2023",
    tenure: "2022–23",
    domain: "Public Health & Policy",
    scope: "Critical Infrastructure Logistics Advisory",
    highlight: true
  }
];

const domains = [
  "All",
  "Healthcare Delivery & Equity",
  "Public Health & Policy",
  "Capacity Building & Digital Learning",
  "International Alliances",
  "Sustainability & Climate Change"
];

export default function Workshops() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDomain, setSelectedDomain] = useState("All");

  const filteredWorkshops = useMemo(() => {
    return workshopsData.filter((item) => {
      const matchesDomain = selectedDomain === "All" || item.domain === selectedDomain;
      const q = searchTerm.toLowerCase();
      const matchesSearch =
        item.title.toLowerCase().includes(q) ||
        item.collaborator.toLowerCase().includes(q) ||
        item.type.toLowerCase().includes(q) ||
        item.date.toLowerCase().includes(q) ||
        item.scope.toLowerCase().includes(q);

      return matchesDomain && matchesSearch;
    });
  }, [searchTerm, selectedDomain]);

  return (
    <div className="container py-5 min-vh-100 position-relative text-light" style={{ zIndex: 1 }}>
      
      {/* 1. Header Banner */}
      <div className="row align-items-center g-4 mb-5">
        <div className="col-12 col-lg-8" data-aos="fade-right">
          <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill mb-3 sub-badge">
            <HeartPulse size={16} className="text-cyan" />
            <span className="small text-light fw-medium">Policy Dialogue & Capacity Building</span>
          </div>

          <h1 className="display-5 fw-bold text-white mb-3">
            Workshops & <span className="text-gradient">Strategic Roundtables</span>
          </h1>

          <p className="lead text-secondary fs-6 mb-4 pe-lg-3">
            Convening national health consortia, international bilateral forums, and high-level stakeholder dialogues 
            in collaboration with premier global agencies including the <strong>William J. Clinton Foundation</strong>, 
            <strong> Sabin Vaccine Institute</strong>, <strong>Empower School of Health</strong>, and the <strong>Government of India</strong>.
          </p>

          {/* Quick Metrics Bar */}
          <div className="d-flex flex-wrap gap-3">
            <div className="p-3 rounded-3 glass-card-sm text-center" style={{ minWidth: '110px' }}>
              <span className="fs-4 fw-bold text-cyan">11+</span>
              <span className="small text-secondary d-block" style={{ fontSize: '0.75rem' }}>Summits & Roundtables</span>
            </div>
            <div className="p-3 rounded-3 glass-card-sm text-center" style={{ minWidth: '110px' }}>
              <span className="fs-4 fw-bold text-warning">Global Consortia</span>
              <span className="small text-secondary d-block" style={{ fontSize: '0.75rem' }}>India & Africa Initiatives</span>
            </div>
            <div className="p-3 rounded-3 glass-card-sm text-center" style={{ minWidth: '110px' }}>
              <span className="fs-4 fw-bold text-info">2022 – 2024</span>
              <span className="small text-secondary d-block" style={{ fontSize: '0.75rem' }}>Active Convening</span>
            </div>
          </div>
        </div>

        {/* Global Impact Card */}
        <div className="col-12 col-lg-4" data-aos="fade-left">
          <div className="p-4 rounded-4 glass-panel border-glow text-center d-flex flex-column align-items-center justify-content-center h-100">
            <Globe2 size={42} className="text-cyan mb-3" />
            <h5 className="text-white fw-bold mb-2">Public Health Leadership</h5>
            <p className="text-secondary small mb-0 lh-base">
              Disseminating actionable evidence across oxygen supply chains, AMR mitigation, HPV/cervical cancer elimination, and climate transitions.
            </p>
          </div>
        </div>
      </div>

      {/* 2. Search & Filter Bar */}
      <div className="p-4 rounded-4 glass-panel mb-5" data-aos="fade-up">
        <div className="row g-3 align-items-center justify-content-between">
          
          {/* Search Box */}
          <div className="col-12 col-md-5">
            <div className="position-relative">
              <Search size={18} className="text-secondary position-absolute top-50 start-0 translate-middle-y ms-3" />
              <input
                type="text"
                placeholder="Search by topic, partner organization, or date..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-control rounded-pill ps-5 bg-dark text-light border border-secondary border-opacity-50 py-2 small"
              />
            </div>
          </div>

          {/* Result Count */}
          <div className="col-12 col-md-auto text-md-end text-secondary small">
            Showing <strong className="text-white">{filteredWorkshops.length}</strong> of {workshopsData.length} strategic events
          </div>
        </div>

        {/* Domain Filter Badges */}
        <div className="d-flex flex-wrap gap-2 mt-3 pt-3 border-top border-secondary border-opacity-25 align-items-center">
          <div className="d-flex align-items-center gap-1 text-secondary small me-2">
            <Filter size={14} /> Domain:
          </div>
          {domains.map((dom, idx) => {
            const isSelected = selectedDomain === dom;
            return (
              <button
                key={idx}
                type="button"
                onClick={() => setSelectedDomain(dom)}
                className="btn btn-sm rounded-pill px-3 py-1 transition-all"
                style={{
                  fontSize: '0.8rem',
                  fontWeight: isSelected ? '700' : '500',
                  color: isSelected ? '#0a0f1d' : '#94a3b8',
                  background: isSelected ? 'linear-gradient(135deg, #00f2fe, #4facfe)' : 'rgba(255, 255, 255, 0.05)',
                  border: isSelected ? '1px solid #00f2fe' : '1px solid rgba(255, 255, 255, 0.15)',
                  boxShadow: isSelected ? '0 0 14px rgba(0, 242, 254, 0.45)' : 'none'
                }}
              >
                {dom}
              </button>
            );
          })}
        </div>
      </div>

      {/* 3. Workshops Stream */}
      <div className="row g-4">
        <AnimatePresence>
          {filteredWorkshops.map((item, index) => {
            return (
              <motion.div
                layout
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.25 }}
                key={`${item.id}-${index}`}
                className="col-12 col-lg-6"
              >
                <div 
                  className="p-4 rounded-4 glass-panel h-100 d-flex flex-column justify-content-between position-relative overflow-hidden"
                  style={{
                    borderLeft: item.highlight 
                      ? '4px solid #00f2fe' 
                      : '4px solid rgba(255, 255, 255, 0.15)'
                  }}
                >
                  <div>
                    {/* Header Badges */}
                    <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                      <span 
                        className="badge px-3 py-1 rounded-pill small"
                        style={{
                          background: 'rgba(0, 242, 254, 0.08)',
                          color: '#00f2fe',
                          border: '1px solid rgba(0, 242, 254, 0.2)',
                          fontSize: '0.72rem'
                        }}
                      >
                        {item.type}
                      </span>

                      <span className="small text-warning fw-semibold d-inline-flex align-items-center gap-1">
                        <Calendar size={13} /> {item.date}
                      </span>
                    </div>

                    {/* Workshop Title */}
                    <h5 className="text-white fw-bold fs-6 mb-2 lh-base">
                      {item.title}
                    </h5>

                    {/* Collaborator / Partner Organization */}
                    <div className="d-flex align-items-start gap-2 text-info small mb-3">
                      <Building2 size={15} className="flex-shrink-0 mt-1" />
                      <span><strong>Partners:</strong> {item.collaborator}</span>
                    </div>
                  </div>

                  {/* Scope & Academic Tenure Footer */}
                  <div className="pt-3 border-top border-secondary border-opacity-15 d-flex align-items-center justify-content-between gap-2 text-secondary small">
                    <span className="text-truncate text-light opacity-75" style={{ fontSize: '0.75rem' }}>
                      {item.scope}
                    </span>
                    <span className="badge bg-dark border border-secondary border-opacity-50 text-warning px-2 py-1" style={{ fontSize: '0.7rem' }}>
                      Academic Year: {item.tenure}
                    </span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>

        {filteredWorkshops.length === 0 && (
          <div className="col-12 text-center py-5">
            <p className="text-secondary fs-5 mb-0">No workshops or strategic events match your search criteria.</p>
          </div>
        )}
      </div>

    </div>
  );
}